/**
 * The garden scene for one biome: what goes where and in what order. How
 * anything looks is up to the current `art` (see art/types.ts).
 */
import { crowdedBy, suggestedSpots, type Biome, type GameState, type Plant, type Planting, type PlantingProblem } from '@thicket/core';
import { drawPlantingAids, type PlantingAids } from './aids.js';
import { art } from './art/index.js';
import { anchorOf, COLS, isPlantable, isWalkable, ROWS, tileAt, tileKey, TILE } from './maps.js';

export { H, W } from './maps.js';
export { drawPlantingAids, type PlantingAids } from './aids.js';

const hashOf = (s: string) => {
  let h = 2166136261;
  for (const ch of s) h = Math.imul(h ^ ch.charCodeAt(0), 16777619);
  return h >>> 0;
};

/** Tiles in this biome where something already grows. */
export function takenTiles(s: GameState, biome: Biome): Set<string> {
  const taken = new Set<string>();
  for (const p of s.plants) {
    if (p.goneAt || p.biome !== biome || p.x === null || p.y === null || p.plot !== null) continue;
    const { c, r } = tileAt(p.x, p.y);
    taken.add(tileKey(c, r));
  }
  return taken;
}

/** Why this tile can't take a planting (not open ground, already taken, too close to a tree), or null if it can. */
export function plantProblem(s: GameState, biome: Biome, c: number, r: number, what?: Planting): PlantingProblem | null {
  if (!isPlantable(art.map(biome), c, r)) return 'ground';
  if (takenTiles(s, biome).has(tileKey(c, r))) return 'taken';
  if (what && crowdedBy(s, biome, c, r, what).length) return 'crowded';
  return null;
}

/** Whether a tap on this tile can plant something. */
export function canPlant(s: GameState, biome: Biome, c: number, r: number, what?: Planting): boolean {
  return plantProblem(s, biome, c, r, what) === null;
}

/** The suggested spots that glow while choosing where a tree goes: the ones it could take right now. */
export function glowSpots(s: GameState, biome: Biome, what: Planting): [number, number][] {
  if (what.kind !== 'tree') return [];
  return suggestedSpots(biome).filter(([c, r]) => plantProblem(s, biome, c, r, what) === null);
}

export const spotFor = (biome: Biome, c: number, r: number) => ({ where: 'ground' as const, biome, ...anchorOf(c, r) });

interface Roamer { x: number; y: number; tx: number | null; ty: number | null; wait: number; flip: boolean }
const roamers = new Map<string, Roamer>();

function roamer(id: string, biome: Biome): Roamer {
  let r = roamers.get(id);
  if (!r) {
    const map = art.map(biome);
    const h = hashOf(id + biome);
    let c = h % COLS;
    let row = (h >>> 8) % ROWS;
    for (let i = 0; i < COLS * ROWS && !isPlantable(map, c, row); i++) {
      c = (c + 7) % COLS;
      if (c < 7) row = (row + 1) % ROWS;
    }
    r = { ...anchorOf(c, row), tx: null, ty: null, wait: 40 + (h % 80), flip: false };
    roamers.set(id, r);
  }
  return r;
}

function step(r: Roamer, biome: Biome, speed: number) {
  if (r.tx === null || r.ty === null) {
    if (--r.wait > 0) return;
    const { c, r: row } = tileAt(r.x, r.y);
    const nc = c + Math.floor(Math.random() * 3) - 1;
    const nr = row + Math.floor(Math.random() * 3) - 1;
    if (isWalkable(art.map(biome), nc, nr)) {
      const a = anchorOf(nc, nr);
      r.tx = a.x;
      r.ty = a.y;
      r.flip = a.x < r.x;
    }
    r.wait = 60 + Math.random() * 140;
    return;
  }
  const dx = r.tx - r.x;
  const dy = r.ty - r.y;
  const d = Math.hypot(dx, dy);
  if (d < speed) {
    r.x = r.tx;
    r.y = r.ty;
    r.tx = r.ty = null;
  } else {
    r.x += (dx / d) * speed;
    r.y += (dy / d) * speed;
  }
}

export interface DrawOptions { aids: PlantingAids | null; today: string; tick: number; reduce: boolean }

export function drawScene(ctx: CanvasRenderingContext2D, s: GameState, biome: Biome, o: DrawOptions) {
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(art.background(biome), 0, 0);
  const frame = { tick: o.tick, reduce: o.reduce };
  art.ambient?.(ctx, biome, frame);

  const queue: { y: number; draw: () => void }[] = [];
  const todos = new Map(s.tasks.filter((t) => t.type === 'todo').map((t) => [t.id, t]));

  // The first six bed plots show on the bed's tiles in the scene, two per tile.
  const bed = art.map(biome).bed;
  if (biome === s.profile.starterBiome && bed.length) {
    for (const p of s.plants) {
      if (p.kind !== 'bed' || p.goneAt || p.plot === null || p.plot >= bed.length * 2) continue;
      const [c, r] = bed[Math.floor(p.plot / 2)]!;
      const x = c * TILE + (p.plot % 2 ? 11 : 5);
      const y = r * TILE + 13;
      queue.push({ y, draw: () => art.bedPlant(ctx, { x, y, stage: p.stage, seed: hashOf(p.id) }) });
    }
  }

  for (const p of s.plants) {
    if (p.goneAt || p.biome !== biome || p.x === null || p.y === null || p.plot !== null) continue;
    const at = { x: p.x, y: p.y };
    if (p.kind === 'tree') {
      queue.push({ y: at.y, draw: () => art.tree(ctx, { ...at, ...frame, species: p.species, stage: p.stage, sparse: p.leaves === 'sparse', biome, seed: hashOf(p.id) % 97 }) });
    } else if (p.kind === 'wildflower') {
      const todo = todos.get(p.taskId ?? '');
      const drooping = !p.bloomed && todo?.type === 'todo' && !!todo.dueDate && todo.dueDate < o.today;
      queue.push({
        y: at.y,
        draw: () => (p.bloomed
          ? art.bloom(ctx, { ...at, ...frame, pace: p.pace, daysLeft: p.bloomDaysLeft ?? 7, biome })
          : art.bud(ctx, { ...at, pace: p.pace, drooping, biome })),
      });
    } else if (p.kind === 'flower') {
      queue.push({ y: at.y, draw: () => art.flower(ctx, { ...at, seed: p.species, biome }) });
    }
  }

  for (const a of s.inventory) {
    if (a.type !== 'animal' || a.biome !== biome) continue;
    const home = a.homePlantId ? s.plants.find((p) => p.id === a.homePlantId && !p.goneAt && p.x !== null && p.y !== null) : null;
    if (home) {
      const { top, mid } = art.treePerch(home.stage, { x: home.x!, y: home.y! });
      const [ax, ay] = a.kind === 'owl' ? mid : top;
      const size = art.animalSize(a.kind);
      const x = ax + (a.kind === 'squirrel' ? -3 : 3);
      queue.push({ y: home.y! + 0.5, draw: () => art.animal(ctx, { x, y: ay + size.h / 2, ...frame, kind: a.kind, flip: a.kind === 'songbird', moving: false }) });
      continue;
    }
    const r = roamer(a.id, biome);
    if (!o.reduce) step(r, biome, a.kind === 'toad' ? 0.15 : 0.3);
    queue.push({
      y: r.y,
      draw: () => {
        const size = art.animalSize(a.kind);
        ctx.fillStyle = 'rgba(30,50,25,.3)';
        ctx.fillRect(Math.round(r.x - size.w / 2) + 1, Math.round(r.y), size.w - 2, 1);
        art.animal(ctx, { x: r.x, y: r.y, ...frame, kind: a.kind, flip: r.flip, moving: r.tx !== null });
      },
    });
  }

  queue.sort((a, b) => a.y - b.y).forEach((q) => q.draw());
  art.overlay?.(ctx, biome, frame);

  if (o.aids) drawPlantingAids(ctx, o.aids, frame);
}

/** What's under a tap: a plant, the bed, or an animal. */
export function hitTest(s: GameState, biome: Biome, x: number, y: number): { plant?: Plant; bed?: boolean; animal?: string } {
  const { c, r } = tileAt(x, y);
  if (art.map(biome).bed.some(([bc, br]) => bc === c && br === r)) return { bed: true };
  let best: { plant: Plant; d: number } | null = null;
  for (const p of s.plants) {
    if (p.goneAt || p.biome !== biome || p.x === null || p.y === null || p.plot !== null) continue;
    const reach = p.kind === 'tree' ? 10 + { seed: 0, small: 4, medium: 8, large: 12, mega: 16 }[p.stage] : 8;
    const dx = Math.abs(x - p.x);
    const dy = p.y - y;
    if (dx > 8 || dy < -3 || dy > reach * 2) continue;
    const d = dx + Math.abs(dy - reach / 2) / 2;
    if (!best || d < best.d) best = { plant: p, d };
  }
  if (best) return { plant: best.plant };
  for (const a of s.inventory) {
    if (a.type !== 'animal' || a.biome !== biome || a.homePlantId) continue;
    const ro = roamers.get(a.id);
    if (ro && Math.abs(ro.x - x) < 8 && ro.y - y < 14 && ro.y - y > -3) return { animal: a.id };
  }
  return {};
}
